import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Hub } from "aws-amplify/utils";
import { fetchAuthSession, fetchUserAttributes, signOut } from "aws-amplify/auth";
import use123zapStore from "../../context/123zapStore";

const AuthContext = createContext(null);

const LAST_KNOWN_KEY = "auth:lastKnown";

function getLastKnownAuth() {
  return localStorage.getItem(LAST_KNOWN_KEY) === "1";
}

function setLastKnownAuth(value) {
  localStorage.setItem(LAST_KNOWN_KEY, value ? "1" : "0");
}

function toBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value == null) return fallback;
  return String(value).toLowerCase() === "true";
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    getLastKnownAuth()
  );

  const [status, setStatus] = useState("booting");
  const [user, setUser] = useState(null);

  /* =========================
     FUNÇÃO PARA EXTRAIR USER
  ========================= */
  const buildUserFromSession = async ({ forceRefresh = false } = {}) => {
    const session = await fetchAuthSession({ forceRefresh });
    const idToken = session?.tokens?.idToken;

    if (!idToken) return null;

    const payload = idToken.payload;
    let attributes = null;

    try {
      attributes = await fetchUserAttributes();
    } catch {
      attributes = null;
    }

    return {
      id: payload.sub,
      username: payload["cognito:username"],
      email: attributes?.email || payload.email || null,
      emailVerified: toBoolean(attributes?.email_verified, toBoolean(payload.email_verified)),
      completeProfile: toBoolean(
        attributes?.["custom:complete_profile"],
        toBoolean(payload["custom:complete_profile"])
      ),
      name: payload.name || null,
      picture: payload.picture || null, // 🔥 AQUI ESTÁ O AJUSTE
    };
  };

  /* =========================
     VALIDAR SESSÃO INICIAL
  ========================= */
  useEffect(() => {
    let isMounted = true;

    async function validateSession() {
      try {
        const builtUser = await buildUserFromSession({ forceRefresh: false });

        if (!isMounted) return;

        if (builtUser) {
          setLastKnownAuth(true);
          setIsAuthenticated(true);
          setUser(builtUser);
          use123zapStore.getState().setFinishedRegister(builtUser.completeProfile);
        } else {
          setLastKnownAuth(false);
          setIsAuthenticated(false);
          setUser(null);
          use123zapStore.getState().clearPlanoAtual();
          use123zapStore.getState().setFinishedRegister(null);
        }
      } catch {
        if (!isMounted) return;

        setLastKnownAuth(false);
        setIsAuthenticated(false);
        setUser(null);
        use123zapStore.getState().clearPlanoAtual();
        use123zapStore.getState().setFinishedRegister(null);
      } finally {
        if (isMounted) {
          setStatus("ready");
        }
      }
    }

    validateSession();

    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      const event = payload?.event;

      if (event === "signedIn") {
        try {
          const builtUser = await buildUserFromSession();
          setLastKnownAuth(true);
          setIsAuthenticated(true);
          setUser(builtUser);
          use123zapStore.getState().setFinishedRegister(
            builtUser?.completeProfile ?? null
          );
        } catch {
          setUser(null);
          use123zapStore.getState().setFinishedRegister(null);
        }
        setStatus("ready");
      }

      if (event === "signedOut") {
        setLastKnownAuth(false);
        setIsAuthenticated(false);
        setUser(null);
        use123zapStore.getState().clearPlanoAtual();
        use123zapStore.getState().setFinishedRegister(null);
        setStatus("ready");
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  /* =========================
     LOGOUT CENTRALIZADO
  ========================= */
  const logout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      throw error;
    }
  };

  const refreshUser = useCallback(async (forceRefresh = true) => {
    try {
      const builtUser = await buildUserFromSession({ forceRefresh });

      if (builtUser) {
        setLastKnownAuth(true);
        setIsAuthenticated(true);
        setUser(builtUser);
        use123zapStore.getState().setFinishedRegister(builtUser.completeProfile);
      } else {
        setLastKnownAuth(false);
        setIsAuthenticated(false);
        setUser(null);
        use123zapStore.getState().setFinishedRegister(null);
      }

      return builtUser;
    } catch (error) {
      console.error("Erro ao atualizar sessão do usuário:", error);
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      status,
      user,
      refreshUser,
      logout,
    }),
    [isAuthenticated, status, user, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
